import { Alert, Button, Card, CardContent, CardHeader, CardTitle } from "@breenova/ui";
import { Download } from "lucide-react";
import { useState } from "react";

import { downloadExport } from "@/lib/exports";

/** The two per-bird documents, shown on the bird's Overview tab. */
export function BirdExportActions({
  birdId,
  ringNumber,
}: {
  birdId: string;
  ringNumber?: string | null;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const slug = (ringNumber ?? "unringed").replace(/[^A-Za-z0-9]+/g, "-").toLowerCase();

  async function run(key: string, path: string, fallbackName: string): Promise<void> {
    setBusy(key);
    setError(null);

    try {
      await downloadExport(path, fallbackName);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The export failed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documents</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-sm">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <div className="flex flex-wrap gap-sm">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              void run(
                "pedigree",
                `/api/v1/birds/${birdId}/pedigree-certificate.pdf`,
                `pedigree-${slug}.pdf`,
              )
            }
          >
            <Download className="size-4" />
            {busy === "pedigree" ? "Preparing…" : "Pedigree certificate"}
          </Button>

          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              void run(
                "provenance",
                `/api/v1/birds/${birdId}/provenance.pdf`,
                `provenance-${slug}.pdf`,
              )
            }
          >
            <Download className="size-4" />
            {busy === "provenance" ? "Preparing…" : "Provenance report"}
          </Button>
        </div>

        {/* Said here as well as on the document itself: a breeder who thinks they are
            holding an official certificate is worse off than one who knows they are not. */}
        <p className="text-body text-on-surface-variant">
          Both are your own records, generated from what you have entered. Neither is a
          permit or a document issued by a federation.
        </p>
      </CardContent>
    </Card>
  );
}
